import { StateControlAbstraction } from "../../state-control.abstraction";
import { inject, signal } from "@angular/core";
import { TransactionView } from "../../../../models/view/transaction/transaction.view";
import { toObservable } from "@angular/core/rxjs-interop";
import { filter } from "rxjs";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { PreferenceView } from "@bitwarden/web-vault/app/models/view/preference/preference.view";
import { ScenarioFilterControlType } from "@bitwarden/web-vault/app/models/types/filter.types";
import { InvalidScenarioAccountTypes } from "@bitwarden/web-vault/app/models/enum/scenario.types";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";

const initialFilter: ScenarioFilterControlType = {
  countries: [],
  accounts: [],
  blockedAccounts: [],
  institutions: [],
  symbols: [],
};

export class ScenarioFilterControl extends StateControlAbstraction {
  private userStore: UserStoreService = inject(UserStoreService);
  protected initialOptions: ScenarioFilterControlType = initialFilter;

  /** Public signal for component to listen too **/
  private _options = signal<ScenarioFilterControlType>(null);
  options = this._options.asReadonly();

  private _selected = signal<ScenarioFilterControlType>(null);
  selected = this._selected.asReadonly();

  clear() {
    this._options.set(null);
    this._selected.set(null);
    this.initialOptions = initialFilter;
  }

  /** **<span style="color: red;">This is for store use only</span>** **/
  readonly selected$ = toObservable(this.selected).pipe(
    filter((x) => x !== null || typeof x !== "undefined"),
  );

  /** On the new transaction load initialise the options for all the transactions. This is the unfiltered list from the transactionStore **/
  initialiseOptions(
    transactions: TransactionView[],
    accounts: AccountView[],
    institutions: InstitutionView[],
    preference: PreferenceView,
  ) {
    this._options.set(this.generateOptions(transactions, accounts, institutions, preference));
    this.initialOptions = { ...this.options() }; // keep a copy for when we reset
  }

  /** Updating control selection **/
  updateSelection(selection: ScenarioFilterControlType) {
    if (this.selected() === null) {
      this._selected.set({ ...this.options() });
    } else {
      this._selected.set({ ...selection });
    }
  }

  clearFilter() {
    this.updateSelection(initialFilter);
    this._options.set({ ...this.initialOptions });
    this._selected.set({ ...this.options() });
  }

  private generateOptions(
    transactions: TransactionView[],
    allAccounts: AccountView[],
    allInstitutions: InstitutionView[],
    preference: PreferenceView,
  ) {
    const countries = [preference.userLocation] as string[];

    const blockedAccounts = allAccounts.reduce((acc: string[], a) => {
      if (!acc.includes(a.id) && this.isInvalidScenarioAccount(a)) {
        acc.push(a.id);
      }
      return acc;
    }, []);

    const institutions = allInstitutions.reduce((acc: string[], i) => {
      const isLocationInstitution = countries.includes(i.swift.countryCode);
      const hasNonBlockedAccount = allAccounts.some(
        (acc) => acc.institutionLink.institutionId === i.id && !blockedAccounts.includes(acc.id),
      );
      if (!acc.includes(i.id) && isLocationInstitution && hasNonBlockedAccount) {
        acc.push(i.id);
      }
      return acc;
    }, []);

    const accounts = transactions.reduce((acc: string[], t) => {
      const account = allAccounts.find((a) => a.id === t.accountLink.id);
      const isProcessed = acc.includes(account.id);
      const isBlocked = blockedAccounts.includes(account.id);
      const isInstitutionListed = institutions.includes(account.institutionLink.institutionId);

      if (!isProcessed && !isBlocked && isInstitutionListed) {
        acc.push(account.id);
      }
      return acc;
    }, []);

    const symbols = transactions.reduce((syms: string[], t) => {
      if (!syms.includes(t.quantity.actualQuantity.symbol)) {
        syms.push(t.quantity.actualQuantity.symbol);
      }
      return syms;
    }, []);

    // Leave the already selected
    return {
      accounts,
      institutions,
      blockedAccounts,
      countries,
      symbols,
    };
  }

  /** @Sinan @Alex Todo : How do we decide on the account to be used for scenarios?
   * Here we need to decide if the account is to be used for scenarios or not
   * */
  isInvalidScenarioAccount(account: AccountView) {
    return InvalidScenarioAccountTypes.includes(account.type);
  }

  applyFiltersOnTransaction(collection: readonly TransactionView[]) {
    return collection.filter((transactionView) => {
      return (
        this.selected()?.accounts.includes(transactionView.accountLink.id ?? "") &&
        this.selected()?.institutions.includes(transactionView.c_institutionLink.id ?? "") &&
        this.selected()?.countries.includes(transactionView.c_country.id ?? "") &&
        this.selected()?.symbols.includes(transactionView.quantity.actualQuantity.symbol || "")
      );
    });
  }

  applyFiltersOnAccounts(collection: readonly AccountView[]) {
    const institutionViews = this.userStore.institutions.institutionViews() ?? [];
    return collection.filter((accountView) => {
      const institutionId = accountView.institutionLink.institutionId ?? "";
      const country =
        institutionViews.find((institution) => institution.id === institutionId)?.swift
          .countryCode ?? "";
      return (
        this.selected()?.accounts.includes(accountView.id ?? "") &&
        this.selected()?.institutions.includes(institutionId) &&
        this.selected()?.countries.includes(country)
      );
    });
  }
}
